import React, { useCallback, useMemo } from 'react'

import { Row, InputNumber, Tooltip, Button } from 'antd'
import { ArrowsAltOutlined, RedoOutlined } from '@ant-design/icons'

import { InputLabel } from '../InputLabel/InputLabel'

interface VTKDataRangeInputProps {
  state: any
  dispatch: (action: object) => void
  ids?: string[]
  disabled?: boolean
}

/*
 * This pattern comes from VTKLayerProperties
 * Not sure how to generalize this yet
 */
const defaultProperties = {
  hasData: {
    default: false,
    get: (item) => {
      const { pointData, cellData } = item.mapper.inputData
      return pointData.numberOfArrays > 0 || cellData.numberOfArrays > 0
    }
  },
  dataRange: {
    default: [0, 100] as [number, number],
    get: (item) => {
      if (!item.mapper.colorByArrayName) return undefined
      return item.mapper.lookupTable.mappingRange
    }
  },
  // this holds the actual data range, not used now, but could be displayed
  dataRangeOptions: {
    default: { 'Solid': { range: [0, 100], colorset: 'Ladybug' } },
    get: (item) => {
      const cellData = item.mapper.inputData.cellData.arrays
      const pointData = item.mapper.inputData.pointData.arrays
      return [...cellData, ...pointData]
    }
  },
} as { [index: string]: any }

export const VTKDataRangeInput: React.FunctionComponent<VTKDataRangeInputProps> = ({ 
  state, 
  dispatch, 
  ids,
  disabled
}) => {

  const layers = useMemo(() => {
    if (!state) return []
    if (!ids) return state.scene
    return state.scene.filter(item => ids.includes(item.id))
  }, [ids, state])

  const getProperty = useCallback((property: string) => (
    layers.reduce((p: any[], c: any) => {
      if (!defaultProperties[property].get) return [defaultProperties[property]['default']]
      const val = defaultProperties[property].get(c.state)
      return typeof val !== 'undefined' ? [...p, val] : p
    }, [])
      .filter((v, i, a) => a.indexOf(v) === i)
      .filter((v, i, a) => v !== null && v !== undefined)
  ), [layers])

  // stands for "layer properties", wanted something short
  const lp = useMemo(() => {

    const properties = Object.keys(defaultProperties).reduce((p: { [index: string]: any }, key) => {
      const prop = getProperty(key)
      if (prop.length === 1) p[key] = prop[0]
      else p[key] = defaultProperties[key].default
      return p
    }, { ...defaultProperties })

    // the following properties need special treatment
    properties.hasData = getProperty('hasData').includes(true)

    return properties
  }, [getProperty])

  return (
    <InputLabel label='Data Range' >
      <Row justify='space-between'
        align='middle'
        style={{ width: '100%', height: '50%' }}
      >
        <InputNumber 
          max={lp.dataRange[1]} 
          step={0.1} 
          keyboard
          precision={1} 
          style={{ width: '30%' }}
          disabled={disabled}
          value={lp.dataRange[0]}
          onChange={(val) => {
            dispatch({ type: 'data-range', ids, value: { colorBy: lp.colorBy, dataRange: [Math.min(val, lp.dataRange[1] - 0.1), lp.dataRange[1]] } })
          }}
        />
        <Tooltip title='Reset to Default'>
          <Button 
            size='small' 
            shape='circle' 
            onClick={() => dispatch({ 
              type: 'reset-data-range', 
              ids, 
              value: lp.colorBy 
            })}
            disabled={disabled}
          >
            <ArrowsAltOutlined rotate={45} />
          </Button>
        </Tooltip>
        <Tooltip title='Reset to Range'>
          <Button 
            size='small' 
            shape='circle' 
            onClick={() => dispatch({ 
              type: 'reset-data-full', 
              ids, 
              value: lp.colorBy 
            })}
            disabled={disabled}
          >
            <RedoOutlined />
          </Button>
        </Tooltip>
        <InputNumber 
          min={lp.dataRange[0] + 0.1} 
          step={0.1} 
          keyboard 
          precision={1} 
          style={{ width: '30%' }}
          disabled={disabled}
          value={lp.dataRange[1]}
          onChange={(val) => {
            dispatch({ type: 'data-range', ids, value: { colorBy: lp.colorBy, dataRange: [lp.dataRange[0], Math.max(val, lp.dataRange[0] + 0.1)] } })
          }}
        />
      </Row>
    </InputLabel>
  )
}